import { useState, useEffect, useCallback } from 'react';
import { QuoteFormState } from '../types';

export interface UseQuoteDraftReturn {
  hasDraft: boolean;
  draftSummary: string;
  draftData: QuoteFormState | null;
  discardDraft: () => void;
  refreshDraft: () => void;
}

export const DRAFT_STORAGE_EVENT = 'rmax:draft-storage-changed';

export function useQuoteDraft(userId?: string): UseQuoteDraftReturn {
  const [draftData, setDraftData] = useState<QuoteFormState | null>(null);

  const checkDraft = useCallback(() => {
    if (typeof window === 'undefined' || !userId) {
      setDraftData(null);
      return;
    }

    try {
      const saved = localStorage.getItem(`rmax_draft_form_${userId}`);
      if (!saved) {
        setDraftData(null);
        return;
      }

      const parsed: QuoteFormState = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object') {
        setDraftData(null);
        return;
      }

      // Validação do Efeito Zeigarnik: considerar rascunho apenas se tiver dados reais iniciados
      const hasClient = Boolean(parsed.clientName && parsed.clientName.trim().length > 0);
      const hasService = Boolean(parsed.serviceName && parsed.serviceName.trim().length > 0);
      const hasProducts = Boolean(parsed.products && parsed.products.length > 0 && parsed.products.some(p => (p.name && p.name.trim().length > 0) || (p.unitValue && p.unitValue > 0)));
      const hasServicesList = Boolean(parsed.services && parsed.services.length > 0 && parsed.services.some(s => (s.name && s.name.trim().length > 0) || (s.value && s.value > 0)));
      const hasPrice = Boolean(parsed.finalPrice && parsed.finalPrice > 0);

      if (hasClient || hasService || hasProducts || hasServicesList || hasPrice) {
        setDraftData(parsed);
      } else {
        setDraftData(null);
      }
    } catch (e) {
      console.warn('Erro ao verificar rascunho de orçamento:', e);
      setDraftData(null);
    }
  }, [userId]);

  useEffect(() => {
    checkDraft();

    const handleStorageChange = () => {
      checkDraft();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);
    window.addEventListener(DRAFT_STORAGE_EVENT, handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
      window.removeEventListener(DRAFT_STORAGE_EVENT, handleStorageChange);
    };
  }, [checkDraft]);

  const discardDraft = useCallback(() => {
    if (typeof window === 'undefined' || !userId) return;
    try {
      localStorage.removeItem(`rmax_draft_form_${userId}`);
      localStorage.removeItem('rmax_draft_items');
      setDraftData(null);
      window.dispatchEvent(new CustomEvent(DRAFT_STORAGE_EVENT));
    } catch (e) {
      console.error('Erro ao descartar rascunho de orçamento:', e);
    }
  }, [userId]);

  // Montagem do resumo amigável de tarefa incompleta
  const draftSummary = (() => {
    if (!draftData) return '';
    if (draftData.clientName && draftData.clientName.trim().length > 0) {
      return `Cliente: ${draftData.clientName.trim()}`;
    }
    if (draftData.serviceName && draftData.serviceName.trim().length > 0) {
      return draftData.serviceName.trim();
    }
    if (draftData.products && draftData.products.length > 0 && draftData.products[0]?.name) {
      const remaining = draftData.products.length - 1;
      return remaining > 0 
        ? `${draftData.products[0].name} (+${remaining} ${remaining === 1 ? 'item' : 'itens'})`
        : draftData.products[0].name;
    }
    if (draftData.services && draftData.services.length > 0 && draftData.services[0]?.name) {
      return draftData.services[0].name;
    }
    return 'Orçamento em andamento';
  })();

  return {
    hasDraft: Boolean(draftData),
    draftSummary,
    draftData,
    discardDraft,
    refreshDraft: checkDraft
  };
}
